import React from 'react';
import styles from './FloatingMenu.scss';
import classNames from 'classnames';

// Generic floating menu option interface
export interface FloatingMenuOption {
  label: string;
  value: string;
  description?: string;
  testId?: string;
}

export interface FloatingMenuOptionProps {
  option: FloatingMenuOption;
  onSelect: (optionValue: string) => void;
  isMenuOpen: boolean;
  className?: string;
}

const FloatingMenuOption = ({ option, onSelect, isMenuOpen, className }: FloatingMenuOptionProps) => {
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, optionValue: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onSelect(optionValue);
    }
  };

  return (
    <div
      key={option.value}
      data-testid={option.testId || `floating-menu-option-${option.value}`}
      className={className || styles.FloatingMenu__Item}
      onClick={() => onSelect(option.value)}
      onKeyDown={event => onKeyDown(event, option.value)}
      tabIndex={isMenuOpen ? 0 : -1}
    >
      {option.label}
    </div>
  );
};

// Generic configurable floating menu component
interface ConfigurableFloatingMenuProps {
  options: FloatingMenuOption[];
  onOptionSelect: (optionValue: string) => void;
  isMenuOpen: boolean;
  menuDirection?: 'up' | 'down';
  containerClassName?: string;
  wrapperClassName?: string;
  itemClassName?: string;
  testId?: string;
  role?: string;
  menuAlignment?: 'left' | 'right';
}

export const ConfigurableFloatingMenu = ({
  options,
  onOptionSelect,
  isMenuOpen,
  menuDirection = 'down',
  menuAlignment = 'left',
  containerClassName,
  wrapperClassName,
  itemClassName,
  testId = 'configurable-floating-menu',
  role = 'menu',
}: ConfigurableFloatingMenuProps) => {
  return (
    <div
      className={classNames(containerClassName || styles.FloatingMenu, {
        [styles.FloatingMenu__MenuDown]: menuDirection === 'down',
        [styles.FloatingMenu__OpenAnimation]: isMenuOpen,
        [styles.FloatingMenu__AlignRight]: menuAlignment === 'right',
        [styles.FloatingMenu__AlignLeft]: menuAlignment === 'left',
      })}
      data-testid={testId}
      aria-expanded={isMenuOpen}
      role={role}
    >
      <div
        className={classNames(wrapperClassName || styles.FloatingMenu__Wrapper, {
          [styles.FloatingMenu__Wrapper__MenuUp]: menuDirection === 'up',
        })}
        data-testid={`${testId}-wrapper`}
      >
        <div className={styles.FloatingMenu__Container}>
          {options.map(option => (
            <FloatingMenuOption
              key={option.value}
              option={option}
              onSelect={onOptionSelect}
              isMenuOpen={isMenuOpen}
              className={itemClassName}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
